import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CalendarClock, CalendarOff, Trash2, Copy, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import PhotoManager from "../../components/business/PhotoManager";
import SpecialHours from "../../components/business/SpecialHours";
import { SOCIAL_NETWORKS } from "../../components/business/SocialLinks";
import { Card, Badge } from "../../components/ui/Card";
import { useAuth } from "../../context/AuthContext";
import { COUNTRIES, RWANDA_CITIES } from "../../lib/locations";

const RUNNING_BEHIND_PRESETS = [10, 20, 30];

const socialForm = (links) => Object.fromEntries(SOCIAL_NETWORKS.map(({ key }) => [key, links?.[key] || ""]));

function MyScheduleSection() {
  const [staffMember, setStaffMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showTimeOff, setShowTimeOff] = useState(false);
  const [newTimeOffDate, setNewTimeOffDate] = useState("");

  const load = () =>
    api
      .get("/owner/staff")
      .then(({ data }) => setStaffMember(data[0] || null))
      .catch(() => setStaffMember(null))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const setRunningBehind = async (minutes) => {
    if (!staffMember) return;
    setUpdating(true);
    try {
      await api.post(`/owner/staff/${staffMember.id}/running-behind`, { minutes });
      await load();
    } finally {
      setUpdating(false);
    }
  };

  const [showSpecialHours, setShowSpecialHours] = useState(false);

  const addTimeOff = async () => {
    if (!staffMember || !newTimeOffDate) return;
    await api.post(`/owner/staff/${staffMember.id}/time-off`, { blocked_date: newTimeOffDate });
    setNewTimeOffDate("");
    load();
  };

  const removeTimeOff = async (timeOffId) => {
    if (!staffMember) return;
    await api.delete(`/owner/staff/${staffMember.id}/time-off/${timeOffId}`);
    load();
  };

  if (loading || !staffMember) return null;

  return (
    <div className="mt-8 border-t border-border pt-6">
      <h2 className="mb-2 font-montserrat text-lg font-bold text-slate-900">My schedule</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        As a freelance account you manage your own availability directly — no separate staff list.
      </p>
      <Card className="p-4">
        {staffMember.running_behind_minutes > 0 ? (
          <div className="mb-3 flex items-center justify-between">
            <Badge variant="warning">
              <Clock className="mr-1 h-3 w-3" /> Running {staffMember.running_behind_minutes} min behind
            </Badge>
            <button className="text-xs text-muted-foreground underline" onClick={() => setRunningBehind(0)} disabled={updating}>
              Clear
            </button>
          </div>
        ) : (
          <p className="mb-3 text-xs text-muted-foreground">On schedule</p>
        )}
        <div className="flex gap-2">
          {RUNNING_BEHIND_PRESETS.map((minutes) => (
            <Button key={minutes} size="sm" variant="outline" disabled={updating} onClick={() => setRunningBehind(minutes)}>
              +{minutes} min
            </Button>
          ))}
        </div>

        <div className="mt-4 border-t border-border pt-4">
          <button
            className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
            onClick={() => setShowTimeOff((v) => !v)}
          >
            <CalendarOff className="h-3.5 w-3.5" /> Time off
          </button>
          {showTimeOff && (
            <div className="mt-2 space-y-2">
              <div className="flex flex-wrap gap-2">
                {(staffMember.availability ?? []).filter((a) => a.is_blocked).map((a) => (
                  <Badge key={a.id} variant="default" className="gap-1">
                    {a.blocked_date?.slice(0, 10)}
                    <button onClick={() => removeTimeOff(a.id)} className="ml-1 hover:text-destructive">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {(staffMember.availability ?? []).filter((a) => a.is_blocked).length === 0 && (
                  <p className="text-xs text-muted-foreground">No blocked dates.</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input type="date" value={newTimeOffDate} onChange={(e) => setNewTimeOffDate(e.target.value)} className="w-auto" />
                <Button size="sm" variant="outline" onClick={addTimeOff}>Block date</Button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-3 border-t border-border pt-3">
          <button
            className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
            onClick={() => setShowSpecialHours((v) => !v)}
          >
            <CalendarClock className="h-3.5 w-3.5" /> Special hours
          </button>
          {showSpecialHours && <SpecialHours staffMember={staffMember} />}
        </div>
      </Card>
    </div>
  );
}

// The shop's public link, shuwa.app/{slug} — owners share it on Instagram,
// TikTok and WhatsApp so clients land straight on their booking page.
function ShopLinkField({ slug, onChange, error, hiddenReason }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/${slug}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — the link is still visible to copy by hand.
    }
  };

  return (
    <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
      <label htmlFor="shop-link" className="mb-1 block text-sm font-semibold text-slate-900">
        Your shop link
      </label>
      <div className="flex items-center overflow-hidden rounded-xl border border-input bg-white focus-within:border-primary">
        <span className="shrink-0 pl-3 text-sm text-muted-foreground">{window.location.host}/</span>
        <input
          id="shop-link"
          value={slug}
          onChange={(e) => onChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
          className="h-11 min-w-0 flex-1 bg-transparent pr-3 text-sm outline-none"
        />
      </div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      {hiddenReason && (
        <p className="mt-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {hiddenReason} Until then the link shows "Shop not found" to clients — only you can preview it while logged in.
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={copy}>
          <Copy className="h-4 w-4" /> {copied ? "Copied!" : "Copy link"}
        </Button>
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 text-sm font-medium text-primary">
          <ExternalLink className="h-4 w-4" /> Open
        </a>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Put it in your Instagram and TikTok bio. Changing it breaks links you've already shared.
      </p>
    </div>
  );
}

// Taking a break: hides the shop from search and its link without deleting
// anything. Saves straight away, separately from the form below.
function VisibilityToggle({ paused, busy, error, onToggle }) {
  return (
    <div className="rounded-2xl border border-border p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">Visible on SHUWA</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {paused
              ? "Your shop is paused. Clients can't find it, book new appointments or buy gift cards, packages or memberships. Bookings you already have stay on your calendar, and SHUWA Pro time is frozen until you switch back on."
              : "Taking a break? Switch this off to hide your shop from search and stop new bookings and purchases — nothing is deleted, SHUWA Pro time is frozen while you're away, and you can switch it back on any time."}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!paused}
          aria-label="Visible on SHUWA"
          disabled={busy}
          onClick={onToggle}
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-60 ${paused ? "bg-slate-300" : "bg-primary"}`}
        >
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${paused ? "left-0.5" : "left-[22px]"}`} />
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function BusinessSettings() {
  const { logout, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [businessType, setBusinessType] = useState(null);
  const [hiddenReason, setHiddenReason] = useState("");
  const [paused, setPaused] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);
  const [pauseError, setPauseError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErrors, setSaveErrors] = useState(null);
  const [portfolioPhotos, setPortfolioPhotos] = useState([]);
  const [venuePhotos, setVenuePhotos] = useState([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api
      .get("/owner/business")
      .then(({ data }) => {
        setForm({
          name: data.name || "",
          description: data.description || "",
          phone: data.phone || "",
          whatsapp_number: data.whatsapp_number || "",
          email: data.email || "",
          address: data.address || "",
          city: data.city || "",
          refund_policy: data.refund_policy || "moderate",
          known_name: data.known_name || "",
          tin: data.tin || "",
          category_id: data.category_id || "",
          work_mode: data.work_mode || "on_site",
          country: data.country || "",
          social_links: socialForm(data.social_links),
          slug: data.slug || "",
        });
        setPortfolioPhotos(data.portfolio_photo_urls || []);
        setVenuePhotos(data.venue_photo_urls || []);
        setBusinessType(data.business_type || "business");
        setPaused(Boolean(data.is_paused));
        setHiddenReason(
          data.status === "pending"
            ? "Your shop is waiting for SHUWA to approve it."
            : data.status === "suspended"
              ? "Your shop has been suspended by SHUWA."
              : data.billing_hold_at
                ? "Your shop is hidden until your SHUWA invoice is paid."
                : ""
        );
      })
      .catch(() => setLoadError(true));
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, []);

  if (loadError) {
    return (
      <p className="text-sm text-destructive">
        Couldn't load your business settings. Please refresh the page or log in again.
      </p>
    );
  }
  if (!form) return <p className="text-muted-foreground">Loading…</p>;

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteAccount();
      navigate("/");
    } catch (err) {
      setDeleteError(
        err?.response?.data?.message || "Could not delete your account. Please try again or contact support@shuwa.app."
      );
      setDeleting(false);
    }
  };

  const togglePause = async () => {
    setTogglingPause(true);
    setPauseError("");
    try {
      const { data } = await api.put("/owner/business", { is_paused: !paused });
      setPaused(Boolean(data.is_paused));
    } catch {
      setPauseError("Couldn't update your shop's visibility. Please try again.");
    } finally {
      setTogglingPause(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setSaveErrors(null);
    try {
      const { data } = await api.put("/owner/business", form);
      // Show the links as saved (handles become full URLs).
      setForm((f) => ({ ...f, social_links: socialForm(data.social_links) }));
      setSaved(true);
    } catch (err) {
      setSaveErrors(err?.response?.data?.errors || {});
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl">
      <h1 className="mb-4 font-montserrat text-2xl font-bold text-slate-900">Business settings</h1>
      <form onSubmit={submit} className="space-y-4">
        <ShopLinkField
          slug={form.slug}
          onChange={(slug) => setForm({ ...form, slug })}
          error={saveErrors?.slug?.[0]}
          hiddenReason={hiddenReason || (paused ? "Your shop is paused until you switch it back on." : "")}
        />
        <VisibilityToggle paused={paused} busy={togglingPause} error={pauseError} onToggle={togglePause} />
        <Input placeholder="Business name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <textarea
          placeholder="Description"
          rows={4}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full rounded-xl border border-border p-3 text-sm outline-none focus:border-primary"
        />
        <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <Input placeholder="WhatsApp number" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} />
        <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <select
          aria-label="Country"
          value={form.country}
          onChange={(e) => setForm({ ...form, country: e.target.value, city: e.target.value === form.country ? form.city : "" })}
          className="h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary"
        >
          <option value="">Country</option>
          {COUNTRIES.map((c) => (
            <option key={c.name} value={c.name}>{c.name}</option>
          ))}
        </select>
        {form.country === "Rwanda" ? (
          // A Rwandan shop picks its town from the list, so customers find it by town.
          <select
            aria-label="City"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary"
          >
            <option value="">City / town</option>
            {form.city && !RWANDA_CITIES.includes(form.city) && <option value={form.city}>{form.city}</option>}
            {RWANDA_CITIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        ) : (
          <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Category</label>
          <select
            value={form.category_id}
            onChange={(e) => setForm({ ...form, category_id: e.target.value })}
            className="h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary"
          >
            <option value="">Select a category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {saveErrors?.category_id && <p className="mt-1 text-xs text-destructive">{saveErrors.category_id[0]}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">How do you work?</label>
          <select
            value={form.work_mode}
            onChange={(e) => setForm({ ...form, work_mode: e.target.value })}
            className="h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary"
          >
            <option value="on_site">On site</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
        <Input
          placeholder={businessType === "freelance" ? "Known as (nickname)" : "Known name (if different from legal name)"}
          value={form.known_name}
          onChange={(e) => setForm({ ...form, known_name: e.target.value })}
        />
        {businessType !== "freelance" && (
          <div>
            <Input
              placeholder="TIN (9-digit Tax Identification Number)"
              inputMode="numeric"
              maxLength={9}
              value={form.tin}
              onChange={(e) => setForm({ ...form, tin: e.target.value.replace(/\D/g, "").slice(0, 9) })}
            />
            {saveErrors?.tin && <p className="mt-1 text-xs text-destructive">{saveErrors.tin[0]}</p>}
          </div>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Refund policy</label>
          <select
            value={form.refund_policy}
            onChange={(e) => setForm({ ...form, refund_policy: e.target.value })}
            className="h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary"
          >
            <option value="flexible">Flexible — 100% refund</option>
            <option value="moderate">Moderate — 50% refund</option>
            <option value="strict">Strict — no refund after confirmation</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Social media</label>
          <p className="mb-2 text-xs text-muted-foreground">
            Paste a link or @handle — these show as icons on your public profile.
          </p>
          <div className="space-y-2">
            {SOCIAL_NETWORKS.map(({ key, label, placeholder, Icon }) => (
              <div key={key}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0 text-slate-500" />
                  <Input
                    aria-label={label}
                    placeholder={`${label} — ${placeholder}`}
                    value={form.social_links[key]}
                    onChange={(e) => setForm({ ...form, social_links: { ...form.social_links, [key]: e.target.value } })}
                  />
                </div>
                {saveErrors?.[`social_links.${key}`] && (
                  <p className="mt-1 pl-6 text-xs text-destructive">{saveErrors[`social_links.${key}`][0]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {saveErrors && <p className="text-sm text-destructive">Couldn't save — please check the fields above.</p>}
        {saved && <p className="text-sm text-secondary">Saved.</p>}
        <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
      </form>

      <div className="mt-8 space-y-6 border-t border-border pt-6">
        <PhotoManager
          gallery="portfolio"
          title="Portfolio"
          hint="Showcase photos of your work — shown on your public profile."
          urls={portfolioPhotos}
          onChange={setPortfolioPhotos}
        />
        <PhotoManager
          gallery="venue"
          title="Venue photos"
          hint="Photos of your space, shown at the top of your profile."
          urls={venuePhotos}
          onChange={setVenuePhotos}
        />
      </div>

      {businessType === "freelance" && <MyScheduleSection />}

      <div className="mt-8 border-t border-border pt-6">
        <Button variant="outline" onClick={logout}>Log out</Button>
      </div>

      <div className="mt-10 border-t border-border pt-6">
        <h2 className="mb-2 font-montserrat text-lg font-bold text-slate-900">Delete account</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Permanently deletes your business profile, services, staff, bookings, and payment history. This
          can't be undone. If you have upcoming appointments or pending payouts, cancel or resolve them
          first.
        </p>
        {deleteError && <p className="mb-3 text-sm text-red-600">{deleteError}</p>}
        {!confirmingDelete ? (
          <Button variant="danger" onClick={() => setConfirmingDelete(true)}>Delete my account</Button>
        ) : (
          <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-800">
              Are you sure? This permanently deletes your business account and all of its data.
            </p>
            <p className="text-sm text-red-800">
              Do you have any upcoming appointments or unpaid payouts on this account?
            </p>
            <div className="flex gap-3">
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Yes, delete it"}
              </Button>
              <Button variant="outline" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
